export interface ContentData {
    uuid?: string
    image?: string
    img_main?: string
    resource_type: string
    public_id?: string
    file_format?: string
    source: 'cloudinary' | 'aws'
    backup_url?: string | null
}

interface Transformations {
    [key: string]: any
}

const TRANSFORMATION_KEY = {
    QUALITY: 'quality',
    FORMAT: 'format',
    X: 'x',
    Y: 'y',
    ASPECT_RATIO: 'aspect_ratio',
    RADIUS: 'radius',
    START_OFFSET: 'start_offset',
    FLAG: 'flag',
}

const TRANSFORMATION = {
    [TRANSFORMATION_KEY.QUALITY]: 'q',
    [TRANSFORMATION_KEY.FORMAT]: 'f',
    [TRANSFORMATION_KEY.X]: 'x',
    [TRANSFORMATION_KEY.Y]: 'y',
    [TRANSFORMATION_KEY.ASPECT_RATIO]: 'ar',
    [TRANSFORMATION_KEY.RADIUS]: 'r',
    [TRANSFORMATION_KEY.START_OFFSET]: 'so',
    [TRANSFORMATION_KEY.FLAG]: 'fl',
}

// s3 url for old backup media from manual cloudinary migration
export const buildS3Url = (data: ContentData): string => {
    // url sample: https://d3rvz8o7q0uxja.cloudfront.net/image/upload/q_auto%3Abest/submission/LjCz9mcNKUGApj7TusENBk.jpg
    const { resource_type: resourceType, public_id: publicId } = data
    const fileType = resourceType === 'image' ? 'jpg' : 'mp4'
    return `${process.env.REACT_APP_CLOUDFRONT_URL}${resourceType}/upload/q_auto:best/${publicId}.${fileType}`
}

const cloudinaryBaseUrl = 'https://res.cloudinary.com'

/**
 * NOTE: Cloudinary will be migrated completely to AWS
 * We currently have difficult situation due to distributed media bucket: 3 s3 bucket (backup, archive, cloudinary-replacement bucket) and 1 cloudinary
 * Keep the cloudinary utility until we make sure we have been migrated out completely from cloudinary. (After all backend PImage record points 0 to cloudinary)
 */
export const buildMediaUrl = (data: ContentData, transformations?: Transformations, withDefault = true): string => {
    const archiveCloudfrontUrl = process.env.REACT_APP_CLOUDFRONT_URL

    if (data.source === 'aws') {
        // point to backup url for migrated asset from cloudinary to s3 using the auto backup format
        if (data.backup_url) {
            return data.backup_url
        }

        // point to s3 main bucket (new bucket for storing every new upload)
        // condition when source = aws and data image isn't cloudinary, we just render the image url
        if (data.image && !data.image.includes(cloudinaryBaseUrl)) {
            return data.image
        }

        // point to s3 archive bucket, which contains old media from manual cloudinary migration
        if (archiveCloudfrontUrl) {
            return buildS3Url(data)
        }
    }
    const transformationsDefault = withDefault ? { quality: 'auto', format: 'auto' } : {}
    const transformationsWithDefault: Transformations = transformations
        ? { ...transformationsDefault, ...transformations }
        : transformationsDefault

    // cloudinary transformation https://cloudinary.com/documentation/transformation_reference
    let transform = ''
    const keys = Object.keys(transformationsWithDefault)
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i]
        if (i > 0) {
            transform += ','
        }
        transform += `${TRANSFORMATION[key]}_${transformationsWithDefault[key]}`
    }
    transform += '/'

    const { resource_type: resourceType, public_id: publicId } = data
    return `${cloudinaryBaseUrl}/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/${resourceType}/upload/${transform}${publicId}`
}
